/**
 * Document ingestion.
 *
 * The Copilot Studio endpoint takes text, not binaries, so every attachment is
 * reduced to text before it is folded into the outgoing message.
 *
 * Two paths do that reduction. Binary formats go to the MarkItDown API, which
 * returns real Markdown — tables stay tables, headings stay headings — and
 * handles spreadsheets, decks and Outlook messages that no browser library
 * covers. Plain text is read locally, because a round trip would buy nothing.
 *
 * When the API is unconfigured or unreachable, PDFs and .docx fall back to
 * browser parsing. The text is flatter, and the caller is told so rather than
 * being quietly handed worse input.
 */

import { ConvertApiUnavailable, convertRemotely, shouldConvertRemotely } from './convertApi'

export type DocumentKind = 'pdf' | 'word' | 'sheet' | 'slides' | 'email' | 'text'

export interface AttachedDocument {
  id: string
  /** File name, e.g. `Subcontract.pdf`. */
  name: string
  /** Relative path when picked via a folder, otherwise the same as `name`. */
  path: string
  kind: DocumentKind
  bytes: number
  text: string
  truncated: boolean
  /** Set when extraction succeeded but at reduced quality. */
  warning?: string
}

export interface RejectedFile {
  name: string
  reason: string
}

/** Short badge label shown on an attachment chip. */
export const KIND_LABELS: Record<DocumentKind, string> = {
  pdf: 'PDF',
  word: 'DOC',
  sheet: 'XLS',
  slides: 'PPT',
  email: 'MSG',
  text: 'TXT',
}

/** Cap on how many files one message may carry. */
export const MAX_FILES = 20
/** Files above this size are almost certainly scans or the wrong artefact. */
export const MAX_FILE_BYTES = 25 * 1024 * 1024
/**
 * Total characters of document text allowed in a single message. Keeps the
 * activity comfortably inside the Copilot Studio payload limit and inside a
 * sane prompt size for the agent.
 */
export const TOTAL_CHAR_BUDGET = 60_000

const TEXT_EXTENSIONS = [
  '.txt',
  '.md',
  '.markdown',
  '.csv',
  '.tsv',
  '.json',
  '.log',
  '.xml',
  '.htm',
  '.html',
  '.yml',
  '.yaml',
]

/** Formats the browser can parse on its own. */
const LOCAL_BINARY_KINDS: Record<string, DocumentKind> = {
  '.pdf': 'pdf',
  '.docx': 'word',
}

/** Formats that only work when the conversion API is available. */
const REMOTE_ONLY_KINDS: Record<string, DocumentKind> = {
  '.doc': 'word',
  '.xlsx': 'sheet',
  '.xls': 'sheet',
  '.pptx': 'slides',
  '.msg': 'email',
  '.epub': 'text',
}

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot === -1 ? '' : name.slice(dot).toLowerCase()
}

/**
 * Extensions offered in the file picker. Grows when the conversion API is
 * configured, so the picker never advertises a format that would then be
 * silently dropped.
 */
export const ACCEPT_ATTRIBUTE = [
  ...Object.keys(LOCAL_BINARY_KINDS),
  ...Object.keys(REMOTE_ONLY_KINDS).filter((ext) => shouldConvertRemotely(ext)),
  ...TEXT_EXTENSIONS,
].join(',')

function kindOf(file: File): DocumentKind | null {
  const ext = extensionOf(file.name)
  if (ext in LOCAL_BINARY_KINDS) return LOCAL_BINARY_KINDS[ext]
  if (ext in REMOTE_ONLY_KINDS && shouldConvertRemotely(ext)) return REMOTE_ONLY_KINDS[ext]
  if (TEXT_EXTENSIONS.includes(ext)) return 'text'
  // Folder picks and drops often carry no extension but a usable MIME type.
  if (!ext && file.type.startsWith('text/')) return 'text'
  return null
}

function relativePathOf(file: File): string {
  // `webkitRelativePath` is populated for folder picks; drag-and-drop leaves it empty.
  const relative = (file as File & { webkitRelativePath?: string }).webkitRelativePath
  return relative && relative.length > 0 ? relative : file.name
}

/**
 * Splits a raw selection into files worth parsing and files to report back.
 * Folder picks drag in plenty of noise, so unsupported types are filtered
 * silently and only genuine problems are surfaced.
 */
export function triageFiles(
  files: File[],
  alreadyAttached: number,
): { accepted: File[]; rejected: RejectedFile[] } {
  const accepted: File[] = []
  const rejected: RejectedFile[] = []
  let slots = MAX_FILES - alreadyAttached

  for (const file of files) {
    // Hidden files and anything under a dot-directory (.git, .vscode, …).
    if (relativePathOf(file).split('/').some((segment) => segment.startsWith('.'))) continue
    if (!kindOf(file)) continue

    if (file.size > MAX_FILE_BYTES) {
      rejected.push({ name: file.name, reason: 'larger than 25 MB' })
      continue
    }
    if (slots <= 0) {
      rejected.push({ name: file.name, reason: `over the ${MAX_FILES}-file limit` })
      continue
    }

    accepted.push(file)
    slots -= 1
  }

  return { accepted, rejected }
}

function normalize(text: string): string {
  return text
    .replace(/\r\n?/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

async function extractPdf(file: File): Promise<string> {
  const [pdfjs, workerUrl] = await Promise.all([
    import('pdfjs-dist'),
    import('pdfjs-dist/build/pdf.worker.min.mjs?url').then((module) => module.default),
  ])
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl

  const task = pdfjs.getDocument({ data: await file.arrayBuffer() })
  const pdf = await task.promise
  try {
    const pages: string[] = []
    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      const page = await pdf.getPage(pageNumber)
      const content = await page.getTextContent()
      const text = content.items
        .map((item) => ('str' in item ? item.str : ''))
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim()
      if (text) pages.push(`[page ${pageNumber}]\n${text}`)
      page.cleanup()
    }
    return pages.join('\n\n')
  } finally {
    await task.destroy()
  }
}

async function extractWord(file: File): Promise<string> {
  const mammoth = await import('mammoth')
  const result = await mammoth.extractRawText({ arrayBuffer: await file.arrayBuffer() })
  return result.value
}

/** Browser parser for a given extension, or null when there is none. */
function localExtractor(extension: string): ((file: File) => Promise<string>) | null {
  if (extension === '.pdf') return extractPdf
  if (extension === '.docx') return extractWord
  return null
}

export async function readDocument(file: File): Promise<AttachedDocument> {
  const kind = kindOf(file)
  if (!kind) throw new Error(`${file.name} is not a supported document type.`)

  const extension = extensionOf(file.name)
  const fallback = localExtractor(extension)

  let raw: string
  let warning: string | undefined

  if (shouldConvertRemotely(extension)) {
    try {
      raw = await convertRemotely(file)
    } catch (error) {
      // Only infrastructure problems are worth retrying locally. A document the
      // API rejected outright would fail the same way in the browser.
      if (!(error instanceof ConvertApiUnavailable) || !fallback) throw error
      raw = await fallback(file)
      warning = `${file.name} was read with the basic browser parser — layout and tables may be lost.`
    }
  } else if (fallback) {
    raw = await fallback(file)
  } else {
    raw = await file.text()
  }

  const text = normalize(raw)
  if (!text) {
    throw new Error(
      kind === 'pdf'
        ? `${file.name} has no selectable text — it is probably a scan.`
        : `${file.name} appears to be empty.`,
    )
  }

  return {
    id: crypto.randomUUID(),
    name: file.name,
    path: relativePathOf(file),
    kind,
    bytes: file.size,
    text,
    truncated: false,
    warning,
  }
}

/**
 * Trims the combined document text to `TOTAL_CHAR_BUDGET`. Every document gets
 * an equal share; whatever the short ones do not use is handed to the long ones.
 */
export function applyBudget(documents: AttachedDocument[]): AttachedDocument[] {
  const total = documents.reduce((sum, doc) => sum + doc.text.length, 0)
  if (total <= TOTAL_CHAR_BUDGET) return documents

  const caps = new Map<string, number>()
  let remaining = TOTAL_CHAR_BUDGET
  let unresolved = [...documents].sort((a, b) => a.text.length - b.text.length)

  while (unresolved.length > 0) {
    const share = Math.floor(remaining / unresolved.length)
    const fitting = unresolved.filter((doc) => doc.text.length <= share)
    if (fitting.length === 0) {
      for (const doc of unresolved) caps.set(doc.id, share)
      break
    }
    for (const doc of fitting) {
      caps.set(doc.id, doc.text.length)
      remaining -= doc.text.length
    }
    unresolved = unresolved.filter((doc) => doc.text.length > share)
  }

  return documents.map((doc) => {
    const cap = caps.get(doc.id) ?? doc.text.length
    if (doc.text.length <= cap) return doc
    return { ...doc, text: doc.text.slice(0, cap), truncated: true }
  })
}

const DEFAULT_INSTRUCTION =
  'Review the attached document(s) and summarise the key terms, obligations, and risks.'

/** What the user sees in their own bubble when they attach documents without typing. */
export function displayText(text: string, documents: AttachedDocument[]): string {
  const trimmed = text.trim()
  if (trimmed) return trimmed
  return documents.length > 0 ? DEFAULT_INSTRUCTION : ''
}

/** Folds the attached document text into the message the agent receives. */
export function composeAgentMessage(text: string, documents: AttachedDocument[]): string {
  if (documents.length === 0) return text

  const budgeted = applyBudget(documents)
  const sections = budgeted.map((doc, index) => {
    const header = `--- Attached document ${index + 1} of ${budgeted.length}: ${doc.path} ---`
    const footer = doc.truncated
      ? `--- End of excerpt from ${doc.path} (truncated to fit the message limit) ---`
      : `--- End of ${doc.path} ---`
    return `${header}\n${doc.text}\n${footer}`
  })

  return [text.trim() || DEFAULT_INSTRUCTION, ...sections].join('\n\n')
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** Stops a pathological drop (e.g. a home directory) from hanging the tab. */
const MAX_SCANNED_ENTRIES = 500
const MAX_SCAN_DEPTH = 8

function fileOf(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject))
}

function entriesOf(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => reader.readEntries(resolve, reject))
}

async function collectEntry(entry: FileSystemEntry, out: File[], depth: number): Promise<void> {
  if (out.length >= MAX_SCANNED_ENTRIES || depth > MAX_SCAN_DEPTH) return

  if (entry.isFile) {
    const file = await fileOf(entry as FileSystemFileEntry)
    // Keep the folder structure visible to the agent; dropped files lose it otherwise.
    Object.defineProperty(file, 'webkitRelativePath', {
      value: entry.fullPath.replace(/^\//, ''),
      configurable: true,
    })
    out.push(file)
    return
  }

  if (entry.isDirectory) {
    const reader = (entry as FileSystemDirectoryEntry).createReader()
    // readEntries yields at most ~100 entries per call and signals the end with [].
    for (;;) {
      const batch = await entriesOf(reader)
      if (batch.length === 0) break
      for (const child of batch) await collectEntry(child, out, depth + 1)
    }
  }
}

/** Flattens a drop, walking into any dropped folders. */
export async function filesFromDataTransfer(transfer: DataTransfer): Promise<File[]> {
  const entries = Array.from(transfer.items)
    .map((item) => item.webkitGetAsEntry?.())
    .filter((entry): entry is FileSystemEntry => Boolean(entry))

  if (entries.length === 0) return Array.from(transfer.files)

  const files: File[] = []
  for (const entry of entries) await collectEntry(entry, files, 0)
  return files
}
